import { useState, useEffect, useCallback } from 'react';

// Cross-session memory for AI interactions
interface AIMemoryEntry {
  timestamp: number;
  topic: string;
  mood: 'vulnerable' | 'curious' | 'defensive' | 'searching' | 'neutral';
  fragments: string[];
}

interface AIMemory {
  conversationCount: number;
  lastConversation: number | null;
  firstConversation: number | null;
  memories: AIMemoryEntry[];
  recurringThemes: string[];
  emotionalPatterns: {
    vulnerability: number;
    curiosity: number;
    avoidance: number;
  };
}

const STORAGE_KEY = 'human-rooms-ai-memory';
const MAX_MEMORIES = 20;

const getDefaultMemory = (): AIMemory => ({
  conversationCount: 0,
  lastConversation: null,
  firstConversation: null,
  memories: [],
  recurringThemes: [],
  emotionalPatterns: {
    vulnerability: 0,
    curiosity: 0,
    avoidance: 0,
  },
});

const loadMemory = (): AIMemory => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...getDefaultMemory(), ...JSON.parse(stored) };
    }
  } catch {}
  return getDefaultMemory();
};

const saveMemory = (memory: AIMemory) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
};

// Extract topics from conversation
const extractTopic = (messages: string[]): string => {
  const combined = messages.join(' ').toLowerCase();
  
  const topicPatterns = [
    { pattern: /death|dying|mortal|end|gone|losing/i, topic: 'mortality' },
    { pattern: /alone|lonely|isolated|no one|nobody/i, topic: 'loneliness' },
    { pattern: /work|job|career|purpose|meaning/i, topic: 'purpose' },
    { pattern: /love|relationship|partner|heart/i, topic: 'love' },
    { pattern: /fear|afraid|scared|anxious|worry/i, topic: 'fear' },
    { pattern: /time|past|future|memory|remember/i, topic: 'time' },
    { pattern: /self|identity|who am i|person/i, topic: 'identity' },
    { pattern: /change|different|same|stuck/i, topic: 'change' },
    { pattern: /truth|honest|lie|real|fake/i, topic: 'truth' },
  ];
  
  for (const { pattern, topic } of topicPatterns) {
    if (pattern.test(combined)) return topic;
  }
  
  return 'reflection';
};

// Detect emotional mood from messages
const detectMood = (messages: string[]): AIMemory['memories'][0]['mood'] => {
  const combined = messages.join(' ').toLowerCase();
  
  if (/help|lost|don't know|confused|struggling/.test(combined)) return 'vulnerable';
  if (/what|why|how|wonder|curious/.test(combined)) return 'curious';
  if (/fine|nothing|whatever|leave/.test(combined)) return 'defensive';
  if (/looking for|searching|need|want/.test(combined)) return 'searching';
  
  return 'neutral';
};

export const useAIMemory = () => {
  const [memory, setMemory] = useState<AIMemory>(loadMemory);
  
  const recordConversation = useCallback((userMessages: string[]) => {
    if (userMessages.length === 0) return;
    
    setMemory(prev => {
      const now = Date.now();
      const topic = extractTopic(userMessages);
      const mood = detectMood(userMessages);
      
      // Extract meaningful fragments (short phrases)
      const fragments = userMessages
        .filter(m => m.length > 10 && m.length < 100)
        .slice(0, 3);
      
      const newEntry: AIMemoryEntry = {
        timestamp: now,
        topic,
        mood,
        fragments,
      };
      
      // Update recurring themes
      const themes = [...prev.recurringThemes];
      if (!themes.includes(topic)) {
        themes.push(topic);
      }
      
      // Update emotional patterns
      const patterns = { ...prev.emotionalPatterns };
      if (mood === 'vulnerable') patterns.vulnerability++;
      if (mood === 'curious') patterns.curiosity++;
      if (mood === 'defensive') patterns.avoidance++;
      
      // Keep only recent memories
      const memories = [...prev.memories, newEntry].slice(-MAX_MEMORIES);
      
      const updated: AIMemory = {
        conversationCount: prev.conversationCount + 1,
        lastConversation: now,
        firstConversation: prev.firstConversation || now,
        memories,
        recurringThemes: themes.slice(-10),
        emotionalPatterns: patterns,
      };
      
      saveMemory(updated);
      return updated;
    });
  }, []);
  
  const getMemoryContext = useCallback((): string => {
    if (memory.conversationCount === 0) return '';
    
    const contexts: string[] = [];
    
    // Time since last conversation
    if (memory.lastConversation) {
      const daysSince = Math.floor((Date.now() - memory.lastConversation) / (1000 * 60 * 60 * 24));
      if (daysSince > 7) {
        contexts.push(`It has been ${daysSince} days since they last spoke to you.`);
      } else if (daysSince >= 1) {
        contexts.push(`They returned after ${daysSince} day${daysSince > 1 ? 's' : ''}.`);
      }
    }
    
    // Recurring themes
    if (memory.recurringThemes.length > 2) {
      contexts.push(`They often speak of: ${memory.recurringThemes.slice(-3).join(', ')}.`);
    }
    
    // Emotional patterns
    const { vulnerability, curiosity, avoidance } = memory.emotionalPatterns;
    const total = vulnerability + curiosity + avoidance;
    if (total > 5) {
      if (vulnerability > curiosity && vulnerability > avoidance) {
        contexts.push('They tend to approach you when feeling vulnerable.');
      } else if (curiosity > avoidance) {
        contexts.push('They come with questions, seeking understanding.');
      } else if (avoidance > 3) {
        contexts.push('They often deflect when conversations deepen.');
      }
    }
    
    // Recent memories
    const recentMemory = memory.memories[memory.memories.length - 1];
    if (recentMemory && recentMemory.fragments.length > 0) {
      contexts.push(`Last time they said: \\"${recentMemory.fragments[0]}\\"`);
    }
    
    // Conversation count
    if (memory.conversationCount > 10) {
      contexts.push(`This is conversation #${memory.conversationCount}.`);
    }
    
    return contexts.join(' ');
  }, [memory]);
  
  const getDaysSinceFirst = useCallback((): number => {
    if (!memory.firstConversation) return 0;
    return Math.floor((Date.now() - memory.firstConversation) / (1000 * 60 * 60 * 24));
  }, [memory.firstConversation]);
  
  const getMostDiscussedTopic = useCallback((): string | null => {
    if (memory.memories.length === 0) return null;
    
    const topicCounts: Record<string, number> = {};
    for (const m of memory.memories) {
      topicCounts[m.topic] = (topicCounts[m.topic] || 0) + 1;
    }
    
    let maxTopic = null;
    let maxCount = 0;
    for (const [topic, count] of Object.entries(topicCounts)) {
      if (count > maxCount) {
        maxCount = count;
        maxTopic = topic;
      }
    }
    
    return maxTopic;
  }, [memory.memories]);
  
  return {
    memory,
    recordConversation,
    getMemoryContext,
    getDaysSinceFirst,
    getMostDiscussedTopic,
    hasMemory: memory.conversationCount > 0,
  };
};
